/**
 * Recorte adaptativo del ROI (004 · US1 · T096; research R-2/R-4 corregidos por T001).
 *
 * # Por qué existe
 *
 * El recorte era fijo: el 80 % del lado menor del fotograma, siempre. Eso funciona con un marcador
 * en tableta montada, donde el plato llena buena parte del encuadre. **No funciona con el marcador
 * corriendo en un teléfono**, que es el negocio real: su plato mide ~5.4 cm y a 30 cm ocupa el
 * 17.5 % del lado corto del fotograma, lo que deja el símbolo en **3.49 px/módulo** a la entrada
 * del decodificador —el borde exacto del suelo de T001— y en **3.00** a 35 cm, ya por debajo.
 * En hardware (Huawei P30 lite contra el marcador) eso se vio como `frames_observed: 0` en todos
 * los intentos mientras el Bootstrap Code —estático, con segundos de reintentos— sí se leía.
 *
 * Recortar más ajustado **multiplica los píxeles por módulo sin pedirle al trabajador que se
 * acerque** (por debajo de 20 cm la lente gran angular de los iPhone Pro no enfoca, research R-5)
 * **y sin depender del marcador**.
 *
 * # Por qué adaptativo y no un recorte fijo más pequeño
 *
 * Con `s` = fracción del lado corto del fotograma que ocupa el **símbolo**, los píxeles por módulo
 * a la entrada son `400·s / (fracción · 25)`. El rango de `s` que hay que cubrir va de ~0.11
 * (plato de teléfono a 35 cm) a ~0.46 (plato de monitor a 25 cm): **razón 4**. La ventana de
 * trabajo de T001 (3.5–10) solo cubre **razón 2.86**. Ninguna fracción fija cubre las dos puntas:
 * la que salva al teléfono revienta el techo con el monitor y al revés. Medido, celda a celda, en
 * `scratchpad/decoder-bench/src/bench-adaptive-roi.mjs`.
 *
 * # Cómo decide
 *
 * 1. **Barrido** mientras no se ha leído nada: recorre `ROI_SEARCH_STEPS` de la más abierta a la
 *    más cerrada y vuelve (ida y vuelta, sin saltos), 300 ms por peldaño. Va de abierto a cerrado
 *    a propósito: un plato grande decodifica en los primeros peldaños y uno pequeño en los
 *    últimos, y el barrido **para en cuanto lee algo**, así que en el caso normal se recorre poco.
 * 2. **Enganche** en cuanto hay una lectura: el decodificador devuelve el lado del símbolo en la
 *    imagen reducida, así que la fracción que lo llevaría al objetivo sale de una regla de tres
 *    —`fracción · lado / 150`— sin necesidad de saber el tamaño del fotograma ni la distancia.
 *    La señal llega **antes de la secuencia**: el Bootstrap Code se lee primero (ver
 *    `TARGET_SYMBOL_SIDE_PX` para por qué medir sobre el bootstrap sirve para el challenge).
 * 3. **Histéresis** del 15 %: un recorte enganchado solo se mueve si la medida se desvía de eso.
 *    Sin ella el marco de enfoque «respiraría» en cada lectura.
 * 4. **Soltado** si pasan `ROI_RELOCK_MS` (1.5 s) sin leer nada: se vuelve a barrer, **desde el
 *    peldaño más cercano al recorte actual**, para que el marco no pegue un salto.
 *
 * El controlador no conoce píxeles de cámara ni el tamaño del fotograma: trabaja solo en
 * fracciones y en el lado del símbolo que ve el decodificador. Eso lo hace exacto (no hay ningún
 * modelo óptico dentro) y trivial de probar.
 */
import {
  clampRoiFraction,
  ROI_LOCK_HYSTERESIS,
  ROI_RELOCK_MS,
  ROI_SEARCH_STEP_MS,
  ROI_SEARCH_STEPS,
  TARGET_SYMBOL_SIDE_PX,
} from './roi';

/** Lo que el controlador decide para la vuelta actual del bucle. */
export interface RoiDecision {
  /** Fracción del lado menor del fotograma. */
  fraction: number;
  /** `true` si sale de una medida del símbolo; `false` si es un peldaño del barrido. */
  locked: boolean;
}

/** Una lectura, vista por el controlador. */
export interface RoiObservation {
  /** Lado del símbolo en la imagen **reducida** (lo que devuelve el decodificador). */
  symbolSidePx: number;
  /** Fracción con la que se capturó **esa** lectura (no la vigente: pueden diferir). */
  roiFraction: number;
  /** Reloj monotónico del momento de la lectura. */
  nowMs: number;
}

export interface RoiController {
  /** Fracción que hay que usar en esta vuelta del bucle. */
  decide(nowMs: number): RoiDecision;
  /** Alimenta el enganche con una lectura. Ignora las que no traen geometría. */
  observe(observation: RoiObservation): void;
}

export interface RoiControllerOptions {
  /**
   * Fija la fracción y desactiva barrido y enganche. Es el interruptor de emergencia y lo que usan
   * las pruebas que quieren un recorte estable.
   */
  fixedFraction?: number;
  steps?: readonly number[];
  stepMs?: number;
  relockMs?: number;
  hysteresis?: number;
}

/** Índice del peldaño más cercano a `fraction` (para reanudar el barrido sin saltos). */
function nearestStepIndex(steps: readonly number[], fraction: number): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < steps.length; index += 1) {
    const distance = Math.abs((steps[index] ?? 0) - fraction);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  }
  return best;
}

/**
 * Índice de ida y vuelta: `0,1,…,n-1,n-2,…,1` y otra vez. Evita el salto de la última fracción a
 * la primera, que en pantalla se vería como un parpadeo del marco.
 */
function pingPongIndex(length: number, step: number): number {
  if (length <= 1) {
    return 0;
  }
  const period = 2 * length - 2;
  const position = ((step % period) + period) % period;
  return position < length ? position : period - position;
}

export function createRoiController(options: RoiControllerOptions = {}): RoiController {
  const steps = options.steps ?? ROI_SEARCH_STEPS;
  const stepMs = options.stepMs ?? ROI_SEARCH_STEP_MS;
  const relockMs = options.relockMs ?? ROI_RELOCK_MS;
  const hysteresis = options.hysteresis ?? ROI_LOCK_HYSTERESIS;
  const fallback = steps[0] ?? 0.9;

  if (options.fixedFraction !== undefined) {
    const fixed = options.fixedFraction;
    return {
      decide: () => ({ fraction: fixed, locked: true }),
      observe: () => undefined,
    };
  }

  let sweepStartedAtMs: number | null = null;
  let sweepOffset = 0;
  let lockedFraction: number | null = null;
  let lastReadAtMs = 0;

  const startSweep = (nowMs: number, fromFraction: number): void => {
    sweepStartedAtMs = nowMs;
    sweepOffset = nearestStepIndex(steps, fromFraction);
  };

  return {
    decide(nowMs) {
      if (lockedFraction !== null) {
        if (nowMs - lastReadAtMs <= relockMs) {
          return { fraction: lockedFraction, locked: true };
        }
        // Se perdió el código: se vuelve a barrer desde donde está el marco, no desde el principio.
        startSweep(nowMs, lockedFraction);
        lockedFraction = null;
      }
      if (sweepStartedAtMs === null) {
        sweepStartedAtMs = nowMs;
      }
      const elapsed = Math.max(0, nowMs - sweepStartedAtMs);
      const step = Math.floor(elapsed / stepMs) + sweepOffset;
      return { fraction: steps[pingPongIndex(steps.length, step)] ?? fallback, locked: false };
    },

    observe({ symbolSidePx, roiFraction, nowMs }) {
      if (!Number.isFinite(symbolSidePx) || symbolSidePx <= 0) {
        return;
      }
      if (!Number.isFinite(roiFraction) || roiFraction <= 0) {
        return;
      }
      lastReadAtMs = nowMs;
      // Regla de tres: el lado del símbolo en la imagen reducida es inversamente proporcional al
      // lado del recorte, así que la fracción que lo deja en 150 px sale directa.
      const desired = clampRoiFraction(roiFraction * (symbolSidePx / TARGET_SYMBOL_SIDE_PX));
      if (lockedFraction !== null) {
        const drift = Math.abs(desired - lockedFraction) / lockedFraction;
        if (drift <= hysteresis) {
          return;
        }
      }
      lockedFraction = desired;
      sweepStartedAtMs = null;
      sweepOffset = 0;
    },
  };
}
